import { Stack } from '@cmg/design-system';
import { Form, FormikProvider, useFormik, yupToFormErrors } from 'formik';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import { useFirmKeyOrIdContext } from '../../../../../../contexts/FirmKeyOrIdContext';
import { useIndicationActivitySalesAndTradingContext } from '../../../../context/IndicationActivitySalesAndTradingContext';
import InvestorContactInformation from '../../../investor-contact-information/layout/InvestorContactInformation';
import { IndicationSectionWithCreateButtons } from '../../common/indication-section-create-view/IndicationSectionWithCreateButtons';
import IndicationSectionForm from '../../common/indication-section-form/IndicationSectionForm';
import { PassIndicationDialog } from '../../common/PassIndicationDialog';
import {
  createIndicationValidationSchema,
  CreateIndicationValidationSchemaContext,
  IndicationCreateFormValues,
} from '../../common/utils/IndicationSectionValidation';
import { usePassOnCoveredIndication } from '../common/pass-covered-indication-dialog/hooks/usePassOnCoveredIndication';
import { useCreateCoveredIndication } from './hooks/useCreateCoveredIndication';
import { createInitialValues } from './IndicationSectionCreateView.model';

export type Props = Readonly<{
  investorContactMandatory: boolean;
  displayMissingContacts: boolean;
  isInvestorContactRequired: boolean;
}>;

const EMPTY_ARRAY = [];
const IndicationSectionCreateView: React.FC<Props> = ({
  investorContactMandatory,
  displayMissingContacts,
  isInvestorContactRequired,
}) => {
  const { offeringId, basicPublishedOffering, loading, orderBookConfiguration, publishedOffering } =
    useIndicationActivitySalesAndTradingContext();
  const offering = basicPublishedOffering!;
  const { firmKeyOrId } = useFirmKeyOrIdContext();

  const [isPassDialogOpen, setIsPassDialogOpen] = React.useState(false);

  const [createCoveredIndication] = useCreateCoveredIndication(offeringId);

  const formik = useFormik<IndicationCreateFormValues>({
    initialValues: createInitialValues(offering),
    validate: async values => {
      try {
        await createIndicationValidationSchema.validate(values, {
          abortEarly: false,
          context: {
            offeringType: offering.type,
            interestLevels: values.interestLevels,
            isLimitIndicationsOnlyEnabled: orderBookConfiguration.isLimitIndicationsOnlyEnabled,
            isInvestorContactRequired,
            displayMissingContacts,
            createMode: true,
            isIndicationPriceAsPercentageParEnabled:
              orderBookConfiguration.isIndicationPriceAsPercentageParEnabled,
            isOnlySharesUnitOnIndicationsEnabled:
              orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled,
          } satisfies CreateIndicationValidationSchemaContext,
        });

        return {};
      } catch (e) {
        return yupToFormErrors(e);
      }
    },
    onSubmit: createCoveredIndication,
  });
  const { isSubmitting, handleSubmit, handleReset, values } = formik;
  const isLoading = loading || isSubmitting;

  const [onPass, isPassing] = usePassOnCoveredIndication({
    offeringId,
    onClose: () => setIsPassDialogOpen(false),
    isPassingNewIndication: true,
    indicationStatus: null,
  });

  return (
    <IndicationSectionWithCreateButtons
      isLoading={isLoading}
      handlePass={() => setIsPassDialogOpen(true)}
      handleReset={handleReset}
      handleSubmit={handleSubmit}
    >
      <Stack gap={1}>
        <FormikProvider value={formik}>
          <Form>
            <IndicationSectionForm
              filings={defaultTo(publishedOffering?.terms.filings, null)}
              securityType={defaultTo(publishedOffering?.security.type, null)}
              tranches={defaultTo(offering.orderEntryProfile?.tranches, EMPTY_ARRAY)}
              instruments={defaultTo(publishedOffering?.instruments, null)}
              issuerName={defaultTo(publishedOffering?.issuer.name, null)}
              offeringType={offering.type}
              canEnterTranche
              pricingCurrencyCode={offering.pricingCurrencyCode}
              securityTypeDisplayName={offering.securityTypeDisplayName!}
              isLimitIndicationsOnlyEnabled={orderBookConfiguration.isLimitIndicationsOnlyEnabled}
              isOnlySharesUnitOnIndicationsEnabled={
                orderBookConfiguration.isOnlySharesUnitOnIndicationsEnabled
              }
              isIndicationPriceAsPercentageParEnabled={
                orderBookConfiguration.isIndicationPriceAsPercentageParEnabled
              }
            />

            <InvestorContactInformation
              bankInvestorKey={firmKeyOrId}
              sellSideContacts={values.sellSideContacts}
              buySideContacts={[]}
              creating={true}
              isCoveredAccount={true}
              investorContactMandatory={investorContactMandatory}
            />
          </Form>
        </FormikProvider>

        <PassIndicationDialog
          isOpen={isPassDialogOpen}
          isLoading={isPassing}
          onClose={() => setIsPassDialogOpen(false)}
          onPass={onPass}
        />
      </Stack>
    </IndicationSectionWithCreateButtons>
  );
};

export default IndicationSectionCreateView;
